import { useContext, useState } from 'react';
import { LoginAction, LoginStatus, useLoginStatus } from './useLoginStatus';
import { LoginRequestData } from '../../../rest/LoginRequestData';
import { LoginDialogCallback } from './LoginDialogState';
import { validateOTP } from '../../canje/FormularioOtp';
import { useForm } from 'react-hook-form';
import { useLoginRequest } from './useLoginRequest';
import { UserData } from '../../../model/UserData';
import { useLoginToken } from './useLoginToken';
import { MoraDialogContext } from '../../mora/MoraDialogContext';

export const useLoginDialogManager = (onCancel: () => void, afterLogin?: LoginDialogCallback) => {
  const [status, dispatch, isLoading, isRequestError, showEmail, showTokenInput, showConfirmButton, showCancelButton, emailBlocked] =
    useLoginStatus();
  const [requestErrorMessage, setRequestErrorMessage] = useState<string>();
  const [otpInput, setOtpInput] = useState<string | undefined>();
  const [estadoTycs, setEstadoTycs] = useState<boolean>(false);
  const [alreadyAcceptTycs, setAlreadyAcceptTycs] = useState<boolean>(true);
  const [loginToken, setLoginToken, checkLoginToken, resetLoginToken] = useLoginToken();
  const moraDialogState = useContext(MoraDialogContext);

  const {
    register,
    formState: { errors: formErrors },
    reset: resetForm,
    getValues,
    trigger: validateEmail,
  } = useForm<LoginRequestData>({ mode: 'onBlur', defaultValues: { email: '', googleToken: '' } });
  const getEmail = () => getValues('email');

  function reset(cancel: boolean = true) {
    setOtpInput(undefined);
    resetForm();
    resetLoginToken();
    setEstadoTycs(false);
    setRequestErrorMessage(undefined);
    dispatch(LoginAction.REINICIAR);

    if (cancel) onCancel();
  }

  function finishLogin(resp: UserData | undefined) {
    reset();
    afterLogin?.(resp);
  }

  const notifyDefaulter = () => {
    reset(true);
    moraDialogState?.showDialog();
  };

  const [request1, request2] = useLoginRequest(
    showTokenInput,
    dispatch,
    setRequestErrorMessage,
    getEmail,
    setAlreadyAcceptTycs,
    otpInput,
    loginToken,
    setLoginToken,
    finishLogin,
    notifyDefaulter
  );

  const validateForm = async () => {
    if (status === LoginStatus.EMAIL || status === LoginStatus.ERROR_EMAIL_STATUS) return await validateEmail();

    return validateOTP(otpInput);
  };

  function onError(reason?: any) {
    dispatch(LoginAction.ERROR);
    setRequestErrorMessage(reason);
  }

  async function confirm() {
    if (!(await validateForm())) return;

    try {
      switch (status) {
        case LoginStatus.EMAIL:
        case LoginStatus.ERROR_EMAIL_STATUS: {
          dispatch(LoginAction.CONTINUAR);
          request1();
          break;
        }
        case LoginStatus.TOKEN:
        case LoginStatus.ERROR_TOKEN_STATUS: {
          if (!checkLoginToken()) {
            reset(false);
            return;
          }
          setOtpInput(undefined);
          dispatch(LoginAction.CONTINUAR);
          request2();
          break;
        }
      }
    } catch (ex) {
      onError();
    }
  }

  return [
    isLoading,
    isRequestError,
    requestErrorMessage,
    emailBlocked,
    showEmail,
    showTokenInput,
    showConfirmButton,
    showCancelButton,
    reset,
    confirm,
    estadoTycs,
    setEstadoTycs,
    alreadyAcceptTycs,
    otpInput,
    setOtpInput,
    register,
    formErrors,
  ] as const;
};
